/**
 * Hook para buscar estatísticas da fila de campanhas
 * 
 * Utiliza PGMQ nativo do Supabase via RPCs dedicadas
 * que consultam pgmq.q_campaigns e pgmq.a_campaigns
 * 
 * @version 3.0 - PGMQ
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Interface para estatísticas da fila
export interface CampaignQueueStats {
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  avg_latency_ms: number;
}

// Interface para distribuição por campanha
export interface CampaignQueueByCampaign {
  campaign_id: string;
  campaign_name: string;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
}

/**
 * Hook para buscar estatísticas agregadas da fila de campanhas
 * Usa RPC get_campaign_queue_stats_pgmq para consultar PGMQ
 * Filtrado por tenant_id para isolamento multi-tenant
 * 
 * @param tenantId - ID do tenant para filtrar estatísticas
 * @param enabled - Se o hook deve ser habilitado
 */
export function useCampaignQueueStats(tenantId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['campaign-queue-stats-pgmq', tenantId],
    queryFn: async (): Promise<CampaignQueueStats> => {
      if (!tenantId) {
        return {
          pending_count: 0,
          processing_count: 0,
          completed_count: 0,
          failed_count: 0,
          avg_latency_ms: 0,
        };
      }

      // Chamar RPC PGMQ com filtro de tenant_id
      const { data, error } = await supabase.rpc('get_campaign_queue_stats_pgmq', {
        p_tenant_id: tenantId
      });
      
      if (error) {
        throw error;
      }
      
      // RPC PGMQ retorna row única ou array com um item
      const stats = Array.isArray(data) ? data[0] : data;
      
      return {
        pending_count: Number(stats?.pending_count || 0),
        processing_count: Number(stats?.processing_count || 0),
        completed_count: Number(stats?.completed_count || 0),
        failed_count: Number(stats?.failed_count || 0),
        avg_latency_ms: Number(stats?.avg_latency_ms || 0),
      };
    },
    enabled: enabled && !!tenantId,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}

/**
 * Hook para buscar distribuição da fila por campanha
 * Usa RPC get_campaign_queue_by_campaign_pgmq para consultar PGMQ
 * Filtrado por tenant_id para isolamento multi-tenant
 * 
 * @param tenantId - ID do tenant para filtrar estatísticas
 * @param enabled - Se o hook deve ser habilitado
 */
export function useCampaignQueueByCampaign(tenantId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['campaign-queue-by-campaign-pgmq', tenantId],
    queryFn: async (): Promise<CampaignQueueByCampaign[]> => {
      if (!tenantId) {
        return [];
      }

      // Chamar RPC PGMQ com filtro de tenant_id
      const { data, error } = await supabase.rpc('get_campaign_queue_by_campaign_pgmq', {
        p_tenant_id: tenantId
      });
      
      if (error) {
        throw error;
      }
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data || []).map((item: any) => ({
        campaign_id: item.campaign_id,
        campaign_name: item.campaign_name,
        pending_count: Number(item.pending_count || 0),
        processing_count: Number(item.processing_count || 0),
        completed_count: Number(item.completed_count || 0),
        failed_count: Number(item.failed_count || 0),
      }));
    },
    enabled: enabled && !!tenantId,
    refetchInterval: 10000,
    staleTime: 5000,
  });
}
